// src/screens/KioskSlotSelectionScreen.js
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import { kioskAPI } from '../services/api';
import useKioskInactivity from '../hooks/useKioskInactivity';
import { useKioskDraft } from '../context/KioskDraftContext';

const KioskSlotSelectionScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { draft, updateDraft } = useKioskDraft();
  useKioskInactivity();

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const { vehicleId, customerName, customerEmail, customerPhone } = draft;

  useEffect(() => {
    if (!user || user.role !== 'kiosk') {
      navigate('/login');
      return;
    }

    if (!vehicleId || !customerName || !customerEmail || !customerPhone) {
      navigate('/kiosk/book');
      return;
    }
  }, [user, vehicleId, customerName, customerEmail, customerPhone, navigate]);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        setLoading(true);
        setError('');

        const { data } = await kioskAPI.getSlots();

        // Filter and sort available slots
        const now = new Date();
        const availableSlots = (data.availableSlots || [])
          .filter(slot => {
            const slotDate = new Date(slot.date);
            return slotDate >= now && (slot.capacity - slot.bookedCount) > 0;
          })
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (dateA.getTime() !== dateB.getTime()) {
              return dateA - dateB;
            }
            return a.time.localeCompare(b.time);
          });

        setSlots(availableSlots);
      } catch (err) {
        console.error('Error fetching slots:', err);
        setError(err.response?.data?.message || 'Failed to load available slots');
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, []);

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleContinue = () => {
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }

    updateDraft({ date: selectedSlot.date, time: selectedSlot.time });
    navigate('/kiosk/book/confirm');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Group slots by date
  const slotsByDate = slots.reduce((acc, slot) => {
    const dateKey = new Date(slot.date).toDateString();
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(slot);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full">
      <AppHeader title="Select Date & Time" subtitle="Choose an available slot" />

      <div className="flex-1 overflow-y-auto p-4">
        {loading && (
          <div className="text-center text-app-text-secondary-light dark:text-app-text-secondary text-sm py-8">
            Loading available slots...
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        {!loading && slots.length === 0 && (
          <div className="text-center text-app-text-secondary-light dark:text-app-text-secondary text-sm py-8">
            No available slots at this time
          </div>
        )}

        {!loading && slots.length > 0 && (
          <div className="space-y-4">
            {Object.entries(slotsByDate).map(([dateKey, dateSlots]) => (
              <div key={dateKey}>
                <div className="text-sm font-semibold text-app-text-light dark:text-app-text mb-2">
                  {formatDate(dateSlots[0].date)}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {dateSlots.map((slot) => {
                    const isSelected = selectedSlot &&
                      selectedSlot.date === slot.date &&
                      selectedSlot.time === slot.time;

                    return (
                      <button
                        key={`${slot.date}-${slot.time}`}
                        onClick={() => handleSlotSelect(slot)}
                        className={`p-3 rounded-xl text-xs font-semibold transition-colors ${
                          isSelected
                            ? 'bg-app-accent text-black'
                            : 'bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-secondary-light dark:text-app-text-secondary border border-app-border-light dark:border-app-border hover:border-app-accent'
                        }`}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-app-border-light dark:border-app-border">
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90"
          >
            Back
          </button>

          <button
            onClick={handleContinue}
            disabled={!selectedSlot}
            className="flex-1 py-3 rounded-2xl bg-app-accent text-black text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default KioskSlotSelectionScreen;
